import { useEffect, useMemo, useRef, useState } from "react";
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { SimulatorBlock } from "../contract/types";
import { Icon } from "../components/Icon";
import { TexText } from "../components/Math";
import { cssVars } from "../lib/css";
import { remToSvgUnits } from "../lib/rem";
import { seriesColor, useChartTokens } from "../theme/chartTokens";
import {
  SIMULATOR_LABELS,
  computeSimulator,
  resolveCashFlows,
  type CalculatorResult,
  type SimulatorInputs,
  type SimulatorKind,
} from "./calculators";
import { BLOCK_EVENTS } from "./useBlockAction";
import type { BlockViewProps } from "./types";

/**
 * Calculadora interactiva.
 *
 * Todo el cálculo ocurre aquí (`calculators.ts`): el estudiante mueve un
 * control y ve el resultado en el mismo fotograma. Al backend solo le llega
 * `simulator.change` con los inputs —y con retardo, para no inundar el stream
 * mientras se arrastra un deslizador.
 */

const EMIT_DEBOUNCE_MS = 450;

function initialInputs(block: SimulatorBlock): SimulatorInputs {
  const inputs: SimulatorInputs = {};
  for (const input of block.inputs ?? []) {
    if (typeof input.value === "number" && Number.isFinite(input.value)) {
      inputs[input.key] = input.value;
    }
  }
  return inputs;
}

function tolerance(input: { min?: number | null; max?: number | null; step?: number | null }): number {
  const step = input.step ?? null;
  if (typeof step === "number" && step > 0) return step;
  const min = input.min ?? null;
  const max = input.max ?? null;
  if (typeof min === "number" && typeof max === "number" && max > min) {
    return Number(((max - min) / 100).toPrecision(2));
  }
  return 0.1;
}

export function Simulator({ block, emit, interactive }: BlockViewProps<SimulatorBlock>) {
  const kind: SimulatorKind = block.kind;
  const tokens = useChartTokens();
  const inputsSpec = useMemo(() => block.inputs ?? [], [block.inputs]);

  const [values, setValues] = useState<SimulatorInputs>(() => initialInputs(block));
  const [flows, setFlows] = useState<number[]>(() => (block.cash_flows ?? []).slice());
  const [touched, setTouched] = useState(false);

  const timerRef = useRef<number | null>(null);
  const latestRef = useRef<{ inputs: SimulatorInputs; flows: number[] }>({
    inputs: initialInputs(block),
    flows: (block.cash_flows ?? []).slice(),
  });

  useEffect(() => {
    const nextInputs = initialInputs(block);
    const nextFlows = (block.cash_flows ?? []).slice();
    setValues(nextInputs);
    setFlows(nextFlows);
    setTouched(false);
    latestRef.current = { inputs: nextInputs, flows: nextFlows };
  }, [block.id, block]);

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const result: CalculatorResult = useMemo(
    () => computeSimulator(kind, values, flows),
    [kind, values, flows],
  );

  function schedule(inputs: SimulatorInputs, nextFlows: number[]) {
    latestRef.current = { inputs, flows: nextFlows };
    if (!interactive) return;
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      const { inputs: current, flows: currentFlows } = latestRef.current;
      emit(BLOCK_EVENTS.simulatorChange, {
        inputs: { ...current, ...(currentFlows.length > 0 ? { cash_flows: currentFlows } : {}) },
      });
    }, EMIT_DEBOUNCE_MS);
  }

  function setValue(key: string, value: number) {
    setTouched(true);
    setValues((current) => {
      const next = { ...current, [key]: value };
      schedule(next, flows);
      return next;
    });
  }

  function setFlow(index: number, value: number) {
    setTouched(true);
    setFlows((current) => {
      const next = current.slice();
      next[index] = value;
      schedule(values, next);
      return next;
    });
  }

  function reset() {
    const nextInputs = initialInputs(block);
    const nextFlows = (block.cash_flows ?? []).slice();
    setValues(nextInputs);
    setFlows(nextFlows);
    setTouched(false);
    schedule(nextInputs, nextFlows);
  }

  const resolvedFlows = useMemo(() => resolveCashFlows(values, flows), [values, flows]);
  const showFlows = kind === "npv" || kind === "irr";
  const profile = result.profile ?? null;

  return (
    <div className="atom atom--simulator sim" data-touched={touched ? "true" : "false"}>
      <header className="sim__head">
        <span className="sim__kind u-label">{SIMULATOR_LABELS[kind] ?? kind}</span>
        {block.module ? <span className="sim__module">{block.module}</span> : null}
      </header>

      <div className="sim__body">
        <div className="sim__controls">
          {inputsSpec.map((input) => {
            const value = values[input.key] ?? input.value;
            const min = input.min ?? null;
            const max = input.max ?? null;
            const step = tolerance(input);
            const hasSlider = typeof min === "number" && typeof max === "number" && max > min;
            const fill = hasSlider
              ? Math.min(100, Math.max(0, ((value - (min ?? 0)) / ((max ?? 1) - (min ?? 0))) * 100))
              : 0;

            return (
              <div key={input.key} className="sim__field">
                <label className="sim__label" htmlFor={`sim-${block.id}-${input.key}`}>
                  <span className="sim__label-text">{input.label}</span>
                  {input.unit ? <span className="sim__unit">{input.unit}</span> : null}
                </label>
                <div className="sim__control">
                  <input
                    className="sim__number u-nums"
                    id={`sim-${block.id}-${input.key}`}
                    type="number"
                    value={Number.isFinite(value) ? value : 0}
                    min={min ?? undefined}
                    max={max ?? undefined}
                    step={step}
                    onChange={(event) => {
                      const parsed = Number(event.target.value);
                      if (Number.isFinite(parsed)) setValue(input.key, parsed);
                    }}
                  />
                  {hasSlider ? (
                    <input
                      className="sim__slider"
                      type="range"
                      aria-label={`${input.label} (deslizador)`}
                      value={value}
                      min={min ?? 0}
                      max={max ?? 1}
                      step={step}
                      style={cssVars({ "--fill": `${fill}%` })}
                      onChange={(event) => {
                        const parsed = Number(event.target.value);
                        if (Number.isFinite(parsed)) setValue(input.key, parsed);
                      }}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}

          {showFlows && flows.length > 0 ? (
            <div className="sim__field sim__field--flows">
              <span className="sim__label">
                <span className="sim__label-text">Flujos de caja por periodo</span>
                <span className="sim__unit">t = 1…{flows.length}</span>
              </span>
              <div className="sim__flows">
                {flows.map((flow, index) => (
                  <label key={index} className="sim__flow">
                    <span className="sim__flow-label u-nums">t{index + 1}</span>
                    <input
                      className="sim__flow-input u-nums"
                      type="number"
                      step={100}
                      value={flow}
                      onChange={(event) => {
                        const parsed = Number(event.target.value);
                        if (Number.isFinite(parsed)) setFlow(index, parsed);
                      }}
                    />
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          <div className="sim__reset-row">
            <button type="button" className="u-button u-button--ghost u-button--small" onClick={reset}>
              <Icon name="refresh" size="0.75rem" />
              Restablecer
            </button>
          </div>
        </div>

        <div className="sim__output">
          {result.error ? (
            <p className="sim__error">
              <Icon name="alert" size="1rem" />
              <span>{result.error}</span>
            </p>
          ) : null}

          {result.primary ? (
            <div className={`sim__primary sim__primary--${result.primary.tone ?? "neutral"}`}>
              <span className="sim__primary-label">{result.primary.label}</span>
              <span className="sim__primary-value">
                {result.primary.unit ? (
                  <span className="sim__primary-unit">{result.primary.unit}</span>
                ) : null}
                <span className="u-nums">{result.primary.formatted}</span>
              </span>
              {result.primary.verdict ? (
                <span className="sim__primary-verdict">
                  <Icon
                    name={
                      result.primary.tone === "positive"
                        ? "check-circle"
                        : result.primary.tone === "negative"
                          ? "alert"
                          : "info"
                    }
                    size="0.875rem"
                  />
                  <span>{result.primary.verdict}</span>
                </span>
              ) : null}
            </div>
          ) : null}

          {profile ? (
            <div className="sim__profile">
              <span className="sim__profile-label u-label">VAN según la tasa de descuento</span>
              <div className="sim__profile-plot">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={profile.rates.map((rate, index) => ({
                      rate: `${(rate * 100).toFixed(0)}%`,
                      value: profile.values[index] ?? 0,
                    }))}
                    margin={{ top: remToSvgUnits(0.25), right: remToSvgUnits(0.5), bottom: 0, left: 0 }}
                  >
                    <XAxis
                      dataKey="rate"
                      tick={{ fill: tokens.label, fontSize: remToSvgUnits(0.625) }}
                      axisLine={{ stroke: tokens.axis }}
                      tickLine={false}
                      interval="preserveStartEnd"
                      minTickGap={remToSvgUnits(1)}
                    />
                    <YAxis
                      tick={{ fill: tokens.label, fontSize: remToSvgUnits(0.625) }}
                      axisLine={false}
                      tickLine={false}
                      width={remToSvgUnits(2.75)}
                    />
                    <ReferenceLine y={0} stroke={tokens.axis} strokeWidth={remToSvgUnits(0.0625)} />
                    <Tooltip
                      contentStyle={{
                        background: tokens.tooltipSurface,
                        border: `0.0625rem solid ${tokens.grid}`,
                        borderRadius: "0.375rem",
                        fontSize: "0.75rem",
                      }}
                      labelStyle={{ color: tokens.inkSecondary }}
                      itemStyle={{ color: tokens.ink }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      name="VAN"
                      stroke={seriesColor(tokens, 0)}
                      strokeWidth={remToSvgUnits(0.125)}
                      fill={seriesColor(tokens, 0)}
                      fillOpacity={0.1}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}

          {result.rows.length > 0 ? (
            <dl className="sim__rows">
              {result.rows.map((row) => (
                <div
                  key={row.label}
                  className={`sim__row ${row.emphasis ? "sim__row--emphasis" : ""}`}
                >
                  <dt className="sim__row-label">
                    {row.label}
                    {row.hint ? <span className="sim__row-hint">{row.hint}</span> : null}
                  </dt>
                  <dd className="sim__row-value u-nums">{row.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}

          {kind === "irr" || kind === "npv" ? (
            <p className="sim__flows-note">
              Flujo evaluado: {resolvedFlows.map((flow) => flow.toLocaleString("es-PE")).join(" · ")}
            </p>
          ) : null}
        </div>
      </div>

      {block.hint ? (
        <p className="sim__hint">
          <Icon name="lightbulb" size="0.875rem" />
          <TexText text={block.hint} />
        </p>
      ) : null}
    </div>
  );
}

export default Simulator;
